import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Box,
  Chip,
  Collapse,
  FormControl,
  IconButton,
  MenuItem,
  OutlinedInput,
  Typography,
} from "@mui/material";
import { memo, useCallback, useEffect, useState } from "react";
import useColors from "../../../../api/hooks/useColors";
import {
  StyledSelect,
  StyledTextField,
} from "../../../../components/forms/styles/formFieldStyles";
// Separate component for text fields to handle local state with debouncing
import ContainedButton from "../../../../components/buttons/ContainedButton";
import OutlinedButton from "../../../../components/buttons/OutlinedButton";
const TextFieldWithLocalState = memo(
  ({ fieldKey, initialValue, placeholder, onChange, colors }) => {
    const [localValue, setLocalValue] = useState(initialValue || "");

    const DEBOUNCE_DELAY_MS = 1000;
    // Update local value when initial value changes
    useEffect(() => {
      setLocalValue(initialValue || "");
    }, [initialValue, fieldKey]);

    // Debounce the onChange to save after user stops typing
    useEffect(() => {
      const timeoutId = setTimeout(() => {
        if (localValue !== (initialValue || "")) {
          onChange(fieldKey, localValue);
        }
      }, DEBOUNCE_DELAY_MS); // Save after 1 second of no typing

      return () => clearTimeout(timeoutId);
    }, [localValue, fieldKey, onChange, initialValue]);

    const handleChange = (e) => {
      setLocalValue(e.target.value);
    };

    const handleBlur = (e) => {
      // Save immediately on blur
      onChange(fieldKey, e.target.value);
    };

    return (
      <StyledTextField
        fullWidth
        size="small"
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        variant="outlined"
        sx={{
          "& .MuiOutlinedInput-root": {
            backgroundColor: colors.common_white,
          },
        }}
      />
    );
  }
);

TextFieldWithLocalState.displayName = "TextFieldWithLocalState";

const ActionConfigForm = memo(
  ({
    action,
    config = {},
    onConfigChange,
    onClose,
    isExpanded = true,
    onToggleExpand,
    onAddMoreAction,
    onDone,
    showActionButtons = false,
    onActionConfigured,
  }) => {
    const colors = useColors();

    const handleFieldChange = useCallback(
      (fieldKey, value) => {
        const newConfig = { ...config, [fieldKey]: value };
        onConfigChange(action.id, newConfig);
      },
      [config, onConfigChange, action.id]
    );

    const renderField = useCallback(
      (fieldKey, fieldConfig) => {
        const value = config[fieldKey] || "";

        switch (fieldConfig.type) {
          case "text":
          case "key-value":
            return (
              <TextFieldWithLocalState
                fieldKey={fieldKey}
                initialValue={value}
                placeholder={fieldConfig.placeholder}
                onChange={handleFieldChange}
                colors={colors}
              />
            );

          case "select":
            return (
              <FormControl fullWidth size="small">
                <StyledSelect
                  value={value}
                  onChange={(e) => handleFieldChange(fieldKey, e.target.value)}
                  displayEmpty
                  sx={{
                    backgroundColor: colors.common_white,
                  }}
                >
                  <MenuItem value="" disabled>
                    <Typography variant="caption" color={colors.text_secondary}>
                      Select {fieldKey}
                    </Typography>
                  </MenuItem>
                  {fieldConfig.options.map((option) => (
                    <MenuItem key={option} value={option}>
                      <Typography variant="caption" color={colors.text_primary}>
                        {option}
                      </Typography>
                    </MenuItem>
                  ))}
                </StyledSelect>
              </FormControl>
            );

          case "multi-select":
            const selectedValues = Array.isArray(value) ? value : [];
            return (
              <FormControl fullWidth size="small">
                <StyledSelect
                  multiple
                  value={selectedValues}
                  onChange={(e) => handleFieldChange(fieldKey, e.target.value)}
                  input={<OutlinedInput />}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((val) => (
                        <Chip
                          key={val}
                          label={val}
                          size="small"
                          onDelete={() => {
                            const newValue = selected.filter(
                              (item) => item !== val
                            );
                            handleFieldChange(fieldKey, newValue);
                          }}
                          deleteIcon={
                            <CloseIcon
                              style={{
                                fontSize: "14px",
                                color: colors.text_secondary,
                              }}
                            />
                          }
                          sx={{
                            height: "24px",
                            backgroundColor: colors.grey_5percent,
                            "& .MuiChip-deleteIcon": {
                              color: colors.text_secondary,
                              "&:hover": {
                                color: colors.error_main,
                              },
                            },
                          }}
                        />
                      ))}
                    </Box>
                  )}
                  sx={{
                    backgroundColor: colors.common_white,
                  }}
                >
                  {fieldConfig.options.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </StyledSelect>
              </FormControl>
            );

          default:
            return null;
        }
      },
      [config, handleFieldChange, colors]
    );

    return (
      <Box
        sx={{
          padding: 0,
          backgroundColor: "transparent",
          marginBottom: "4px",
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Header - Summary when collapsed */}
        {!isExpanded && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "8px 0",
              cursor: "pointer",
              "&:hover": {
                backgroundColor: colors.grey_2percent,
              },
            }}
            onClick={() => onToggleExpand(action.id)}
          >
            <Typography
              variant="body2"
              sx={{
                color: colors.text_primary,
                flex: 1,
              }}
            >
              {action.name}
              {config.project && ` → ${config.project}`}
              {config.channel && ` → ${config.channel}`}
              {config.recipient && ` → ${config.recipient}`}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton size="small" sx={{ color: colors.text_secondary }}>
                <ExpandMoreIcon />
              </IconButton>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onClose();
                }}
                sx={{
                  color: colors.text_secondary,
                  "&:hover": {
                    backgroundColor: colors.error_5percent,
                    color: colors.error_main,
                  },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        )}

        {/* Configuration Form */}
        <Collapse in={isExpanded}>
          <Box sx={{ marginTop: 2, marginLeft: "32px" }}>
            {action.config &&
              Object.entries(action.config).map(([fieldKey, fieldConfig]) => (
                <Box key={fieldKey} sx={{ marginBottom: 2 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: colors.text_primary,
                      fontWeight: 500,
                      marginBottom: 1,
                    }}
                  >
                    {fieldKey.charAt(0).toUpperCase() +
                      fieldKey.slice(1).replace(/([A-Z])/g, " $1")}{" "}
                    <Typography
                      component="span"
                      sx={{ color: colors.error_main, fontSize: "12px" }}
                    >
                      *
                    </Typography>
                  </Typography>
                  {renderField(fieldKey, fieldConfig)}
                </Box>
              ))}

            {/* Summary */}
            <Box
              sx={{
                marginTop: 3,
                padding: 2,
                backgroundColor: colors.grey_2percent,
                borderRadius: "4px",
                border: `1px solid ${colors.grey_10percent}`,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  marginBottom: 1,
                }}
              >
                <Box
                  sx={{
                    width: "16px",
                    height: "16px",
                    borderRadius: "50%",
                    backgroundColor: colors.info_main,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    sx={{
                      color: colors.common_white,
                      fontSize: "10px",
                      fontWeight: "bold",
                    }}
                  >
                    i
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  sx={{ color: colors.text_primary, fontWeight: 500 }}
                >
                  Summary
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: colors.text_secondary }}>
                {action.name}
                {config.project && `, Project is ${config.project}`}
                {config.issueType && `, Issue type is ${config.issueType}`}
                {config.summary && `, Issue Summary is ${config.summary}`}
                {config.description &&
                  `, Issue description is ${config.description}`}
                {config.assignee && `, Assignee is ${config.assignee}`}
                {config.labels &&
                  Array.isArray(config.labels) &&
                  config.labels.length > 0 &&
                  `, Labels are "${config.labels.join('", "')}"`}
              </Typography>
            </Box>

            {/* Action Buttons */}
            {showActionButtons && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 2,
                  marginTop: 3,
                  paddingTop: 2,
                  borderTop: `1px solid ${colors.grey_10percent}`,
                }}
              >
                <OutlinedButton
                  onClick={() => {
                    onAddMoreAction();
                  }}
                >
                  Add More Action
                </OutlinedButton>

                <ContainedButton
                  onClick={() => {
                    if (onActionConfigured) {
                      onActionConfigured(action.id);
                    }
                    if (onDone) {
                      onDone();
                    }
                  }}
                  color="primary"
                  size="small"
                >
                  Done
                </ContainedButton>
              </Box>
            )}
          </Box>
        </Collapse>
      </Box>
    );
  }
);

ActionConfigForm.displayName = "ActionConfigForm";

export default ActionConfigForm;
